'use client'

import { env } from 'next-runtime-env'
import { useSearchParams } from 'next/navigation'
import { Dispatch, RefObject, SetStateAction, SyntheticEvent, useEffect, useState } from 'react'
import useSWR from 'swr'
import { useEventListener, useTimeout } from 'usehooks-ts'

import ViverseLogoOverlay from '@/app/[locale]/[roomId]/components/ViverseLogoOverlay'
import { SEARCH_PARAM_KEYS } from '@/constants/auth'
import { BI_CATEGORY_BEHAVIOR_IN_ROOM, BI_EVENT_VIEW_COUNT } from '@/constants/bi-sdk'
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe'
import { useAuthContext } from '@/context/AuthContext'
import { useBISdkContext } from '@/context/BISdkContext'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useWorldDetailsContext } from '@/context/WorldDetailsContext'
import { useFullscreen } from '@/hooks/useFullscreen'
import { IframeHandlers } from '@/hooks/useIframeScrollLock'
import {
  getStandaloneIframeAllowPermissions,
  getStandaloneIframeSandboxPermissions,
} from '@/lib/iframe'
import { addWorldViewCount } from '@/services/cms'
import { getSgToken } from '@/services/sg-token'

const VIVERSE_SDK_METHODS = {
  CHECK_AUTH: 'VIVERSE_SDK/checkAuth',
  LOGIN: 'VIVERSE_SDK/login',
  LOGOUT: 'VIVERSE_SDK/logout',
} as const

const isValidOrigin = (origin: string) => {
  const ALLOWED_ORIGINS = (env('NEXT_PUBLIC_IFRAME_POSTMESSAGE_ORIGINS') || '')
    .split(',')
    .map((origin) => origin.trim())
  return ALLOWED_ORIGINS.some((allowedOrigin) => {
    if (allowedOrigin.includes('*')) {
      const pattern = allowedOrigin.replace('*', '.*')
      return new RegExp(`^${pattern}$`).test(origin)
    }
    return allowedOrigin === origin
  })
}

const isValidBase64 = (str: string) => {
  // Check if string contains only valid base64 characters
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
  if (!base64Regex.test(str)) {
    return false
  }

  // Check if length is valid for base64 (must be multiple of 4)
  if (str.length % 4 !== 0) {
    return false
  }

  // Check if string is valid base64 by attempting to decode and re-encode
  try {
    const decoded = atob(str)
    const reEncoded = btoa(decoded)
    return reEncoded === str
  } catch (error) {
    console.error('Failed to decode base64:', error)
    return false
  }
}

type StandaloneIframeProps = {
  setIsShowSplash: Dispatch<SetStateAction<boolean>>
  handlers: IframeHandlers
  ref: RefObject<HTMLIFrameElement | null>
}

function StandaloneIframe({ setIsShowSplash, handlers, ref }: StandaloneIframeProps) {
  const { isFullscreen } = useFullscreen('standalone-iframe-wrapper')
  const { store } = useConfigsContext()
  const { handleLogin, handleLogout } = useAuthContext()
  const { standaloneUrl, iframeSettings, worldMeta } = useWorldDetailsContext()
  const { roomData, setRoomData } = useCheckEnterRoomContext()
  const [iframeUrl, setIframeUrl] = useState('')
  const [isIframeLoaded, setIsIframeLoaded] = useState(false)

  const searchParams = useSearchParams()
  const previewMode = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.PREVIEW)
  const token = searchParams.get(IFRAME_SEARCH_PARAM_KEYS.TOKEN)
  const source = searchParams.get(SEARCH_PARAM_KEYS.SOURCE)
  const appId = searchParams.get(SEARCH_PARAM_KEYS.APP_ID)
  const appState = searchParams.get(SEARCH_PARAM_KEYS.APP_STATE)
  const iframeState = searchParams.get(SEARCH_PARAM_KEYS.STATE)
  const htcTokenFromUrl = searchParams.get(SEARCH_PARAM_KEYS.HTC_TOKEN)
  const htcToken = store?.credentials?.htcToken
  const isFull3D = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.FULL3D)

  const sandboxAttr = getStandaloneIframeSandboxPermissions(iframeSettings).join(' ')
  const allowAttr = getStandaloneIframeAllowPermissions(iframeSettings).join('; ')

  const { sendBIReport } = useBISdkContext()
  const { accountType } = useConfigsContext()

  useSWR(
    source && appId && htcTokenFromUrl && source === 'viverse-sdk' ? 'sg-token' : null,
    () => getSgToken(appId!, htcTokenFromUrl!),
    {
      onSuccess: (data) => {
        const base64Response = btoa(JSON.stringify(data))
        const currentUrl = new URL(iframeUrl!)
        currentUrl.searchParams.set('auth_resp', base64Response)
        if (iframeState) {
          currentUrl.searchParams.set('state', iframeState)
        }
        setIframeUrl(currentUrl.toString())
      },
    },
  )

  const handleAddWorldViewCount = async () => {
    if (roomData?.hub_sid) {
      const data = await addWorldViewCount(roomData.hub_sid)
      if (data?.status === 200) {
        const prevViewCount = roomData?.view_count || 0
        setRoomData((prev) => ({
          ...prev,
          view_count: prev.view_count + 1,
        }))

        sendBIReport({
          ev: BI_EVENT_VIEW_COUNT,
          category: BI_CATEGORY_BEHAVIOR_IN_ROOM,
          a1: accountType,
          a2: location.href,
          a3: roomData.title,
          a4: roomData.hub_sid,
          a12: `${prevViewCount + 1}`,
        })
      }
    }
  }

  const handleMessage = async (event: MessageEvent) => {
    if (!isValidOrigin(event.origin)) {
      console.warn(`Received message from unauthorized origin: ${event.origin}`)
      return
    }

    const { methods, payload } = event.data || {}
    const source = event.source!

    switch (methods) {
      case VIVERSE_SDK_METHODS.CHECK_AUTH: {
        const { appId } = payload || {}
        if (!appId) return
        if (appId !== worldMeta?.scene_sid) {
          source.postMessage(
            {
              methods: 'VIVERSE_SDK/checkAuth:ack',
              payload: { auth_resp: null },
            },
            {
              targetOrigin: event.origin,
            },
          )
          return
        }
        if (!htcToken) {
          source.postMessage(
            {
              methods: 'VIVERSE_SDK/checkAuth:ack',
              payload: { auth_resp: null },
            },
            {
              targetOrigin: event.origin,
            },
          )
          return
        }
        try {
          const sgTokenResult = await getSgToken(appId, htcToken)
          source.postMessage(
            {
              methods: 'VIVERSE_SDK/checkAuth:ack',
              payload: { auth_resp: sgTokenResult },
            },
            {
              targetOrigin: event.origin,
            },
          )
        } catch (error) {
          console.error('Failed to get SG token:', error)
          source.postMessage(
            {
              methods: 'VIVERSE_SDK/checkAuth:ack',
              payload: { auth_resp: null },
            },
            {
              targetOrigin: event.origin,
            },
          )
        }
        break
      }
      case VIVERSE_SDK_METHODS.LOGIN: {
        const { appId, state } = payload || {}
        if (!appId || appId !== worldMeta?.scene_sid) return
        try {
          const loginParams: Record<string, string> = {
            [SEARCH_PARAM_KEYS.SOURCE]: 'viverse-sdk',
            [SEARCH_PARAM_KEYS.APP_ID]: appId,
            ...(state && { [SEARCH_PARAM_KEYS.STATE]: state }),
            ...(previewMode && { [IFRAME_SEARCH_PARAM_KEYS.PREVIEW]: previewMode }),
            ...(token && { [IFRAME_SEARCH_PARAM_KEYS.TOKEN]: token }),
          }
          handleLogin({
            loginParams,
          })
        } catch (error) {
          console.error('Login failed:', error)
        }
        break
      }
      case VIVERSE_SDK_METHODS.LOGOUT: {
        handleLogout()
        break
      }
    }
  }

  useEventListener('message', handleMessage)

  const handleIframeError = (e: SyntheticEvent<HTMLIFrameElement>) => {
    setIsShowSplash(false)
    console.error('Iframe loading failed:', e)
    window.open(iframeUrl, '_blank')
  }

  const handleIframeLoad = () => {
    setIsShowSplash(false)
    setIsIframeLoaded(true)
  }

  useEffect(() => {
    setIsShowSplash(true)
  }, [setIsShowSplash])

  useEffect(() => {
    // Check if current URL contains app_state query parameter
    if (appState && isValidBase64(appState)) {
      // If app_state exists and is valid base64, add it to standaloneUrl
      const standaloneUrlObj = new URL(standaloneUrl)
      standaloneUrlObj.searchParams.set('app_state', appState)
      setIframeUrl(standaloneUrlObj.toString())
    } else {
      setIframeUrl(standaloneUrl)
    }
  }, [standaloneUrl, appState])

  useTimeout(handleAddWorldViewCount, 10000)

  return (
    <div id="standalone-iframe-wrapper" className="relative z-0 h-full w-full">
      <ViverseLogoOverlay
        position={roomData.logo_overlay_position}
        isVisible={isIframeLoaded && (isFullscreen || isFull3D)}
      />
      <iframe
        ref={ref}
        id="standalone-iframe"
        title="Embedded Content"
        className="z-10 size-full border-none bg-transparent"
        src={iframeUrl}
        sandbox={sandboxAttr}
        allow={allowAttr}
        onError={handleIframeError}
        onLoad={handleIframeLoad}
        {...handlers}
      />
    </div>
  )
}

export default StandaloneIframe
